import {type UIManager} from './UIManager.ts'
import {notificationController, uiGameManager} from '../../../shared/scripts/EntryShared.ts'
import {Scene} from './UISceneTypes.ts'
import {UIUtilsEjsVariables} from '../../../shared/scripts/UIUtilsEjsVariables.ts'
import {UIOnlineFriendController} from '../../../shared/scripts/ui_friends/UIOnlineFriendController.ts'
import {type PlayerColor} from '@colonist/game-states'
import {ClientUserState} from '../../../shared/scripts/ClientUserState.ts'

export class UISceneController {
    get scene(): Scene {
        return this._scene
    }

    set scene(value: Scene) {
        this._scene = value
    }

    private _scene: Scene
    private readonly uiManager: UIManager

    constructor(uiManager: UIManager) {
        this.scene = Scene.LandingPage
        this.uiManager = uiManager

        this.toggleSidebarAndMinimalHeaderForLandingPage()

        ClientUserState.registerChangeListener(() => {
            if(this.scene == Scene.LandingPage && UISceneController.shouldShowMainSceneOnUserInit()) {
                this.loadScene(Scene.MainPage)
            }
        })
    }

    private static shouldShowMainSceneOnUserInit(): boolean {
        // if the conditions for showing the main page change, update `first-scene-detection.ejs`
        const userState = ClientUserState.userState
        if(userState == undefined) return false
        if(UIUtilsEjsVariables.isUserOnDiscord) return false
        if(UIUtilsEjsVariables.isUserOnMobileAppOrWeb) return true
        if(userState.isLoggedIn) return true
        if(!userState.hasZeroCompletedGames()) return true

        return false
    }

    static shouldShowLandingPage(): boolean {
        return !UISceneController.shouldShowMainSceneOnUserInit()
    }

    loadScene(scene: Scene, data?: Record<string, unknown>) {
        if(this.scene == Scene.Room && scene != Scene.Room && scene != Scene.Game) {
            this.uiManager.room.resetRoom()
        }

        this.scene = scene
        this.hideAllScenes()

        this.toggleSidebarAndMinimalHeaderForLandingPage()

        switch(scene) {
            case Scene.Lobby:
                this.loadLobbyScene()
                UISceneController.setWindowHistoryState(scene)
                break
            case Scene.Room:
                this.loadRoomScene()
                break
            case Scene.Game:
                this.loadGameScene()
                break
            case Scene.LandingPage:
                this.loadLandingPageScene()
                UISceneController.setWindowHistoryState(scene)
                break
            case Scene.MainPage:
                this.loadMainPageScene()
                UISceneController.setWindowHistoryState(scene)
                break
            case Scene.DiscordLeaderboard:
            case Scene.DiscordStore:
            case Scene.DiscordAbout:
            case Scene.DiscordCareers:
            case Scene.DiscordChampionship25:
            case Scene.DiscordChampionship26:
            case Scene.DiscordCommunity:
            case Scene.DiscordFAQ:
            case Scene.DiscordPrivacyPolicy:
            case Scene.DiscordPatchNotes:
            case Scene.DiscordValues:
            case Scene.DiscordValuesExamples:
            case Scene.DiscordTerms:
            case Scene.DiscordPressKit:
            case Scene.DiscordRefundPolicy:
                this.loadDiscordScene(scene, data)
                break
            case Scene.DiscordProfile:
                void this.loadDiscordProfileScene(data)
                break
            case Scene.DiscordReplay:
                this.loadDiscordReplayScene(data)
                break
            case Scene.DiscordGifting:
                this.loadDiscordGiftScene(data)
                break
        }
    }

    hideAllScenes() {
        this.uiManager.lobby.hide()
        this.uiManager.room.hide()
        this.uiManager.landingPage.hide()
        this.uiManager.homepageSceneContainer.classList.add('hidden')
        this.uiManager.layoutContainer.classList.add('hidden')
        this.uiManager.discordSceneController?.hideAllScenes()
    }

    private static setWindowHistoryState(scene: Scene) {
        if(UISceneController.shouldSetWindowHistoryState(scene)) {
            window.history.replaceState({pageScene: scene}, document.title, window.location.href)
        }
    }

    private static shouldSetWindowHistoryState(scene: Scene): boolean {
        if(window.history.state == null) return true
        if(window.history.state == '') return true
        if(window.history.state.pageScene != scene) return true
        return false
    }

    private loadDiscordScene(scene: Scene, data?: {hash?: string, value?: string}) {
        this.setupDiscordSceneLayout()
        this.uiManager.discordSceneController?.showScene(scene, data?.hash, data?.value)
    }

    private loadLobbyScene() {
        this.uiManager.sidebar.activateLobby()
        this.uiManager.layoutContainer.classList.remove('hidden')
        this.uiManager.layoutContentWithAds.classList.remove('hidden')
        this.uiManager.homepageSceneContainer.classList.remove('hidden')

        UISceneController.setHomepageContainersVisibility(false, true)
        this.setAdSpaceVisibility(true)
        void this.uiManager.adController.showLobbyVerticalAds()
        this.uiManager.lobby.show()
        this.uiManager.header.headerProfileController.initLoginButton('lobby_page')
        UIOnlineFriendController.userOnlineAndIdle()
    }

    private loadRoomScene() {
        this.uiManager.sidebar.activateLobby()
        this.uiManager.layoutContainer.classList.remove('hidden')
        this.uiManager.layoutContentWithAds.classList.add('hidden')

        this.uiManager.room.show()
        notificationController.removeDisconnectBanners()
        UIOnlineFriendController.userEnteredRoom()
    }

    private loadGameScene() {
        this.uiManager.matchmakingController.removePopups()
        this.uiManager.adController.removeLobbyAds()
        notificationController.removeDisconnectBanners()
        this.uiManager.clearDOM()
        uiGameManager.canvasController.buildGame()
        UIOnlineFriendController.userEnteredGame()
    }

    private loadLandingPageScene() {
        this.uiManager.sidebar.activatePlay()
        this.uiManager.layoutContainer.classList.remove('hidden')
        this.uiManager.layoutContentWithAds.classList.add('hidden')
        this.uiManager.homepageSceneContainer.classList.add('hidden')

        UISceneController.setHomepageContainersVisibility(false, false)
        this.setAdSpaceVisibility(false)
        this.uiManager.landingPage.show()
    }

    private loadMainPageScene() {
        // if the elements that are shown here change, update `first-scene-detection.ejs`
        this.uiManager.sidebar.activatePlay()
        this.uiManager.layoutContainer.classList.remove('hidden')
        this.uiManager.layoutContentWithAds.classList.remove('hidden')
        this.uiManager.homepageSceneContainer.classList.remove('hidden')

        const detailsContainer = document.getElementById('mm-details-container') as HTMLDivElement
        const modeSelectorContainer = document.getElementById('mm-mode-selector-container') as HTMLDivElement
        detailsContainer.classList.add('hidden')
        modeSelectorContainer.classList.remove('hidden')

        UISceneController.setHomepageContainersVisibility(true, false)
        this.setAdSpaceVisibility(true)
        void this.uiManager.adController.showLobbyVerticalAds()
        this.uiManager.header.headerProfileController.initLoginButton('main_page')
    }

    private async loadDiscordProfileScene(data?: Record<string, unknown>) {
        this.setupDiscordSceneLayout()

        const username = data?.username as string
        const discordSceneController = this.uiManager.discordSceneController
        if(username != discordSceneController?.discordProfileController.profileController.username) {
            await discordSceneController?.discordProfileController.updatePageContent(username)
        }
        discordSceneController?.showScene(Scene.DiscordProfile)
    }

    private loadDiscordReplayScene(data?: Record<string, unknown>) {
        UISceneController.validateDiscordEnvironment()
        if(data?.gameId == undefined) return
        if(data?.playerColor == undefined) return

        this.setupDiscordSceneLayout()

        const gameId = data.gameId as string
        const playerColor = data.playerColor as PlayerColor

        this.uiManager.discordSceneController?.discordReplayController.loadReplay(gameId, playerColor)
    }

    private loadDiscordGiftScene(data?: Record<string, unknown>) {
        this.setupDiscordSceneLayout()

        const discordSceneController = this.uiManager.discordSceneController
        const giftOverlayController = discordSceneController?.discordStoreController.storeController.bodyController.giftOverlayController

        const recipientUsername = data?.recipientUsername as string | undefined
        const shouldRecipientSelectionBeFirstPage = recipientUsername != undefined
        giftOverlayController?.reorderPages(shouldRecipientSelectionBeFirstPage)
        giftOverlayController?.updateRecipient(recipientUsername)
        discordSceneController?.showScene(Scene.DiscordGifting)
    }

    private static validateDiscordEnvironment(): void {
        if(UIUtilsEjsVariables.isUserOnDiscord) return
        throw new Error('Not on Discord Integration Server')
    }

    private static setHomepageContainersVisibility(isModeSelectorVisible: boolean, isLobbyVisible: boolean): void {
        const modeSelectorContainer = document.getElementById('homepage-mode-selector-container')
        const lobbyContainer = document.getElementById('homepage-lobby-container')

        modeSelectorContainer?.classList.toggle('hidden', !isModeSelectorVisible)
        lobbyContainer?.classList.toggle('hidden', !isLobbyVisible)
    }

    private setAdSpaceVisibility(visible: boolean): void {
        const adSpace = document.getElementById('web-layout-ad-space') as HTMLDivElement

        adSpace.classList.toggle('hidden', !visible)
        this.uiManager.layoutContentWithAds.classList.toggle('no-ads', !visible)
    }

    private setupDiscordSceneLayout(): void {
        UISceneController.validateDiscordEnvironment()
        this.uiManager.layoutContainer.classList.remove('hidden')
        this.uiManager.layoutContentWithAds.classList.remove('hidden')
        UISceneController.setHomepageContainersVisibility(false, false)
        this.setAdSpaceVisibility(false)
    }

    private toggleSidebarAndMinimalHeaderForLandingPage(): void {
        if(this.scene == Scene.LandingPage) {
            this.uiManager.sidebar.hide()
            this.uiManager.header.hide()
        } else {
            this.uiManager.sidebar.show()
            this.uiManager.header.show()
        }
    }
}
